import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Line, Bar } from "react-chartjs-2";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  CardFooter,
  Row,
  Col,
  Table,
  Button,
  Badge,
  Alert,
} from "reactstrap";
import { useAuth } from "../contexts/AuthContext";
import { dashboardService } from "../services/dashboardService";
import { empresasService } from "../services/empresasService";
import Loading from "../components/Loading";
import { usePermissions } from "../hooks/usePermissions";
import PermissionGuard from "../components/PermissionGuard";
import SnowFallComponent from "components/SnowFallComponent";

function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canView } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [estadisticas, setEstadisticas] = useState({
    totalVentasMes: 0,
    totalIngresosMes: 0,
    productosActivos: 0,
    clientesActivos: 0,
    productosStockBajo: 0,
  });

  const [ventasPorMes, setVentasPorMes] = useState({
    labels: [],
    datos: [],
  });
  const [ventasRecientes, setVentasRecientes] = useState([]);
  const [productosStockBajo, setProductosStockBajo] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError("");

      // Obtener empresa activa (como se hace en otros componentes)
      let empresaId = user?.EmpresaID || user?.empresaID || null;

      if (!empresaId) {
        const empresasResponse = await empresasService.getAll();
        if (empresasResponse.success && empresasResponse.data) {
          const empresasData = Array.isArray(empresasResponse.data) ? empresasResponse.data : [];
          const empresaActiva = empresasData.find(emp => {
            const activo = emp.Activo !== undefined ? emp.Activo : (emp.activo !== undefined ? emp.activo : true);
            return activo === true || activo === 1;
          }) || empresasData[0];
          if (empresaActiva) {
            empresaId = empresaActiva.EmpresaID || empresaActiva.empresaID;
          }
        }
      }

      // Obtener sucursal del usuario si tiene
      const sucursalId = user?.SucursalID || user?.sucursalID || null;

      // Cargar estadísticas
      const statsResponse = await dashboardService.getEstadisticas(sucursalId, empresaId);
      if (statsResponse.success) {
        setEstadisticas(statsResponse.data);
      }

      // Cargar ventas por mes
      const ventasMesResponse = await dashboardService.getVentasPorMes(sucursalId, empresaId);
      if (ventasMesResponse.success) {
        setVentasPorMes(ventasMesResponse.data);
      }

      // Cargar ventas recientes
      const ventasRecientesResponse = await dashboardService.getVentasRecientes(sucursalId, 5);
      if (ventasRecientesResponse.success) {
        setVentasRecientes(ventasRecientesResponse.data);
      }

      // Cargar productos con stock bajo
      const stockBajoResponse = await dashboardService.getProductosStockBajo(sucursalId, 5);
      if (stockBajoResponse.success) {
        setProductosStockBajo(stockBajoResponse.data);
      }
    } catch (err) {
      setError(err.message || "Error al cargar datos del dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Configuración de gráfica de ventas por mes
  const chartVentasData = (canvas) => {
    const ctx = canvas.getContext("2d");
    const gradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
    gradientStroke.addColorStop(1, "rgba(29,140,248,0.2)");
    gradientStroke.addColorStop(0.4, "rgba(29,140,248,0.0)");
    gradientStroke.addColorStop(0, "rgba(29,140,248,0)");

    return {
      labels: ventasPorMes.labels.length > 0 ? ventasPorMes.labels : ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN"],
      datasets: [
        {
          label: "Ventas",
          fill: true,
          backgroundColor: gradientStroke,
          borderColor: "#1f8ef1",
          borderWidth: 2,
          borderDash: [],
          borderDashOffset: 0.0,
          pointBackgroundColor: "#1f8ef1",
          pointBorderColor: "rgba(255,255,255,0)",
          pointHoverBackgroundColor: "#1f8ef1",
          pointBorderWidth: 20,
          pointHoverRadius: 4,
          pointHoverBorderWidth: 15,
          pointRadius: 4,
          data: ventasPorMes.datos.length > 0 ? ventasPorMes.datos : [0, 0, 0, 0, 0, 0],
        },
      ],
    };
  };

  const chartVentasOptions = {
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    tooltips: {
      backgroundColor: "#f5f5f5",
      titleFontColor: "#333",
      bodyFontColor: "#666",
      bodySpacing: 4,
      xPadding: 12,
      mode: "nearest",
      intersect: 0,
      position: "nearest",
    },
    responsive: true,
    scales: {
      yAxes: {
        barPercentage: 1.6,
        gridLines: {
          drawBorder: false,
          color: "rgba(29,140,248,0.0)",
          zeroLineColor: "transparent",
        },
        ticks: {
          suggestedMin: 0,
          padding: 20,
          fontColor: "#9a9a9a",
        },
      },
      xAxes: {
        barPercentage: 1.6,
        gridLines: {
          drawBorder: false,
          color: "rgba(29,140,248,0.1)",
          zeroLineColor: "transparent",
        },
        ticks: {
          padding: 20,
          fontColor: "#9a9a9a",
        },
      },
    },
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <div className="content">

        <SnowFallComponent />

        {error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {/* Tarjetas de Estadísticas */}
        <Row>
          <Col lg="3" md="6" sm="6">
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs="5">
                    <div className="icon-big text-center icon-warning">
                      <i className="fas fa-shopping-cart text-warning" />
                    </div>
                  </Col>
                  <Col xs="7">
                    <div className="numbers">
                      <p className="card-category">Ventas del Mes</p>
                      <CardTitle tag="p">{estadisticas.totalVentasMes}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter>
                <hr />
                <div className="stats">
                  <i className="fas fa-dollar-sign" /> Total: {formatCurrency(estadisticas.totalIngresosMes)}
                </div>
              </CardFooter>
            </Card>
          </Col>
          <Col lg="3" md="6" sm="6">
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs="5">
                    <div className="icon-big text-center icon-warning">
                      <i className="fas fa-box text-info" />
                    </div>
                  </Col>
                  <Col xs="7">
                    <div className="numbers">
                      <p className="card-category">Productos Activos</p>
                      <CardTitle tag="p">{estadisticas.productosActivos}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter>
                <hr />
                <div className="stats">
                  <PermissionGuard modulo="Productos" accion="Read">
                    <Button
                      color="link"
                      size="sm"
                      onClick={() => navigate("/admin/productos")}
                    >
                      Ver productos <i className="fas fa-arrow-right" />
                    </Button>
                  </PermissionGuard>
                </div>
              </CardFooter>
            </Card>
          </Col>
          <Col lg="3" md="6" sm="6">
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs="5">
                    <div className="icon-big text-center icon-warning">
                      <i className="fas fa-users text-success" />
                    </div>
                  </Col>
                  <Col xs="7">
                    <div className="numbers">
                      <p className="card-category">Clientes Activos</p>
                      <CardTitle tag="p">{estadisticas.clientesActivos}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter>
                <hr />
                <div className="stats">
                  <PermissionGuard modulo="Clientes" accion="Read">
                    <Button
                      color="link"
                      size="sm"
                      onClick={() => navigate("/admin/clientes")}
                    >
                      Ver clientes <i className="fas fa-arrow-right" />
                    </Button>
                  </PermissionGuard>
                </div>
              </CardFooter>
            </Card>
          </Col>
          <Col lg="3" md="6" sm="6">
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs="5">
                    <div className="icon-big text-center icon-warning">
                      <i className="fas fa-exclamation-triangle text-danger" />
                    </div>
                  </Col>
                  <Col xs="7">
                    <div className="numbers">
                      <p className="card-category">Stock Bajo</p>
                      <CardTitle tag="p">{estadisticas.productosStockBajo}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter>
                <hr />
                <div className="stats">
                  <PermissionGuard modulo="Inventario" accion="Read">
                    <Button
                      color="link"
                      size="sm"
                      onClick={() => navigate("/admin/inventario")}
                    >
                      Ver inventario <i className="fas fa-arrow-right" />
                    </Button>
                  </PermissionGuard>
                </div>
              </CardFooter>
            </Card>
          </Col>
        </Row>

        {/* Gráfica de Ventas por Mes */}
        <Row>
          <Col xs="12">
            <Card className="card-chart">
              <CardHeader>
                <Row>
                  <Col className="text-left" sm="6">
                    <h5 className="card-category">Ventas</h5>
                    <CardTitle tag="h2">Últimos 6 Meses</CardTitle>
                  </Col>
                  <Col sm="6">
                    <div className="btn-group float-right">
                      <PermissionGuard modulo="Ventas" accion="Read">
                        <Button
                          color="info"
                          size="sm"
                          onClick={() => navigate("/admin/ventas")}
                        >
                          Ver todas las ventas
                        </Button>
                      </PermissionGuard>
                    </div>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <div className="chart-area">
                  <Line data={chartVentasData} options={chartVentasOptions} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Tablas de Información */}
        <Row>
          <Col lg="6" md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Productos con Stock Bajo</CardTitle>
              </CardHeader>
              <CardBody>
                {productosStockBajo.length === 0 ? (
                  <Alert color="success">No hay productos con stock bajo</Alert>
                ) : (
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Stock Actual</th>
                        <th>Stock Mínimo</th>
                        <th>Sucursal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productosStockBajo.map((item) => {
                        const producto = item.Producto || item.producto || {};
                        const inventario = item;
                        return (
                          <tr key={inventario.InventarioID || inventario.inventarioID}>
                            <td>{producto.Nombre || producto.nombre || "N/A"}</td>
                            <td>
                              <Badge color="danger">
                                {inventario.StockActual || inventario.stockActual || 0}
                              </Badge>
                            </td>
                            <td>{inventario.StockMinimo || inventario.stockMinimo || 0}</td>
                            <td>{inventario.SucursalNombre || inventario.sucursalNombre || "N/A"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
                <div className="text-center mt-3">
                  <PermissionGuard modulo="Inventario" accion="Read">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => navigate("/admin/inventario")}
                    >
                      Ver Inventario Completo
                    </Button>
                  </PermissionGuard>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg="6" md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Ventas Recientes</CardTitle>
              </CardHeader>
              <CardBody>
                {ventasRecientes.length === 0 ? (
                  <Alert color="info">No hay ventas recientes</Alert>
                ) : (
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Folio</th>
                        <th>Cliente</th>
                        <th>Total</th>
                        <th>Fecha</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ventasRecientes.map((venta) => (
                        <tr key={venta.VentaID || venta.ventaID}>
                          <td>{venta.Folio || venta.folio || `#${venta.VentaID || venta.ventaID}`}</td>
                          <td>{venta.ClienteNombre || venta.clienteNombre || "Cliente General"}</td>
                          <td>{formatCurrency(venta.Total || venta.total || 0)}</td>
                          <td>{formatDate(venta.FechaVenta || venta.fechaVenta)}</td>
                          <td>
                            <Badge color={venta.Estatus === "Cancelada" || venta.estatus === "Cancelada" ? "danger" : "success"}>
                              {venta.Estatus || venta.estatus || "Activa"}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
                <div className="text-center mt-3">
                  <PermissionGuard modulo="Ventas" accion="Read">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => navigate("/admin/ventas")}
                    >
                      Ver Todas las Ventas
                    </Button>
                  </PermissionGuard>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Accesos Directos */}
        <Row>
          <Col xs="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Accesos Rápidos</CardTitle>
              </CardHeader>
              <CardBody>
                <Row>
                  {canView('Ventas') && (
                    <Col md="3" sm="6" className="mb-3">
                      <Button
                        color="primary"
                        block
                        onClick={() => navigate("/admin/ventas")}
                        className="d-flex align-items-center justify-content-center"
                      >
                        <i className="fas fa-shopping-cart mr-2" />
                        Nueva Venta
                      </Button>
                    </Col>
                  )}
                  {canView('Productos') && (
                    <Col md="3" sm="6" className="mb-3">
                      <Button
                        color="info"
                        block
                        onClick={() => navigate("/admin/productos")}
                        className="d-flex align-items-center justify-content-center"
                      >
                        <i className="fas fa-box mr-2" />
                        Productos
                      </Button>
                    </Col>
                  )}
                  {canView('Inventario') && (
                    <Col md="3" sm="6" className="mb-3">
                      <Button
                        color="success"
                        block
                        onClick={() => navigate("/admin/inventario")}
                        className="d-flex align-items-center justify-content-center"
                      >
                        <i className="fas fa-warehouse mr-2" />
                        Inventario
                      </Button>
                    </Col>
                  )}
                  {canView('Reportes') && (
                    <Col md="3" sm="6" className="mb-3">
                      <Button
                        color="warning"
                        block
                        onClick={() => navigate("/admin/reportes")}
                        className="d-flex align-items-center justify-content-center"
                      >
                        <i className="fas fa-chart-bar mr-2" />
                        Reportes
                      </Button>
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default Dashboard;
